import React, { useCallback, useEffect, useState } from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/firestore";
import { API_BASE_URL } from "../../Common/Common";

// Admin Dashboard "Usage & limits" panel for the paid-service gate.
// - Limits: Firestore server_config/limits (public read, admin-only write —
//   see docs/FirebaseRulesPhase2.txt). Defaults come from the server
//   (gate_usage.php returns the effective limits) so an unsaved doc still
//   shows real numbers.
// - PHP vendors' usage: gate_usage.php (needs the admin's Firebase ID token).
// - Photo Book PDF usage is counted by the same PHP gate (the Cloud
//   Function calls gate_check.php), so it arrives in the same report.

const VENDORS = [
  { key: "openai", label: "OpenAI (AI features)" },
  { key: "google_places", label: "Google Places" },
  { key: "openwebninja", label: "OpenWeb Ninja" },
  { key: "hasdata", label: "HasData" },
  { key: "photobook_pdf", label: "Photo Book PDF" },
];

// Must match gate_default_limits() in src/dataBase/api/_gate.php and
// GATE_DEFAULTS in functions/index.js.
const DEFAULT_LIMITS = {
  require_key: false,
  vendors: {
    openai: { enabled: true, daily_total: 2000, per_ip_daily: 60 },
    google_places: { enabled: true, daily_total: 2000, per_ip_daily: 150 },
    openwebninja: { enabled: true, daily_total: 1000, per_ip_daily: 150 },
    hasdata: { enabled: false, daily_total: 500, per_ip_daily: 150 },
    photobook_pdf: { enabled: true, daily_total: 100, per_ip_daily: 10 },
  },
};

const mergeLimits = (doc) => {
  const out = { require_key: !!doc?.require_key, vendors: {} };
  for (const key of Object.keys(DEFAULT_LIMITS.vendors)) {
    out.vendors[key] = { ...DEFAULT_LIMITS.vendors[key], ...(doc?.vendors?.[key] || {}) };
  }
  return out;
};

const utcDate = (offsetDays = 0) => new Date(Date.now() - offsetDays * 86400000).toISOString().slice(0, 10);

const UsageLimitsPanel = ({ activeUsersByDay = {} }) => {
  const [limits, setLimits] = useState(null);
  const [days, setDays] = useState([]);
  const [info, setInfo] = useState({ storage: true, keys_configured: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [showIpsFor, setShowIpsFor] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const db = firebase.firestore();
    // Limits first and independently, so they stay editable even if the
    // usage report below fails.
    try {
      const snap = await db.collection("server_config").doc("limits").get();
      setLimits(mergeLimits(snap.exists ? snap.data() : null));
    } catch (e) {
      setLimits(mergeLimits(null));
      setError("Could not read saved limits — showing defaults. " + (e.message || e));
    }
    try {
      const user = firebase.auth().currentUser;
      if (!user) throw new Error("Sign in with the admin account to see usage.");
      const token = await user.getIdToken();
      const res = await fetch(`${API_BASE_URL}/gate_usage.php`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Usage request failed (${res.status})`);

      setDays(data.days || []);
      setInfo({ storage: data.storage, keys_configured: data.keys_configured });
    } catch (e) {
      setError("Usage report unavailable: " + (e.message || String(e)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setVendorField = (vendor, field, value) => {
    setLimits((prev) => ({
      ...prev,
      vendors: { ...prev.vendors, [vendor]: { ...prev.vendors[vendor], [field]: value } },
    }));
    setMsg("");
  };

  const handleSave = async () => {
    const vendors = {};
    for (const { key } of VENDORS) {
      const v = limits.vendors[key] || {};
      const total = parseInt(v.daily_total, 10);
      const perIp = parseInt(v.per_ip_daily, 10);
      if (!Number.isFinite(total) || total < 0 || !Number.isFinite(perIp) || perIp < 0) {
        setMsg("Limits must be whole numbers, 0 or more (0 = no limit).");
        return;
      }
      vendors[key] = { enabled: !!v.enabled, daily_total: total, per_ip_daily: perIp };
    }
    setSaving(true);
    setMsg("");
    try {
      await firebase.firestore().collection("server_config").doc("limits").set({
        require_key: !!limits.require_key,
        vendors,
        updated: new Date().toISOString(),
        updated_by: firebase.auth().currentUser?.uid || "",
      });
      setMsg("Saved — the gate picks this up within about a minute.");
    } catch (e) {
      setMsg("Failed to save: " + (e.message || e));
    } finally {
      setSaving(false);
    }
  };

  const today = days.find((d) => d.date === utcDate()) || { vendors: {} };

  return (
    <div className="admindash-lookup">
      <p className="tai-paste-title admindash-lookup-title">Usage &amp; limits (paid services)</p>
      <p className="tai-paste-hint">
        Daily caps per service, counted in UTC days. "Per IP" limits one device/network; "Total" caps everyone combined.
        0 means no limit. Turning a service off rejects every request to it.
      </p>
      {error !== "" && <p className="tai-paste-error">{error}</p>}
      {!info.storage && <p className="tai-paste-error">The server can't store usage counts — caps are not being enforced.</p>}
      {loading && !limits && <p className="tai-paste-hint">Loading…</p>}

      {limits && (
        <>
          <table className="admindash-usage-table">
            <thead>
              <tr>
                <th>Service</th>
                <th>On</th>
                <th>Today</th>
                <th>Total cap</th>
                <th>Per-IP cap</th>
                <th>Rejected</th>
                <th>No key</th>
                <th>IPs</th>
              </tr>
            </thead>
            <tbody>
              {VENDORS.map(({ key, label }) => {
                const v = limits.vendors[key] || {};
                const u = today.vendors[key] || {};
                const used = u.total || 0;
                const cap = parseInt(v.daily_total, 10) || 0;
                const near = cap > 0 && used >= cap * 0.8;
                return (
                  <tr key={key}>
                    <td>{label}</td>
                    <td>
                      <input type="checkbox" checked={!!v.enabled} onChange={(e) => setVendorField(key, "enabled", e.target.checked)} />
                    </td>
                    <td className={near ? "admindash-usage-near" : ""}>
                      {used}
                      {cap > 0 ? ` / ${cap}` : ""}
                    </td>
                    <td>
                      <input
                        className="admindash-usage-input"
                        type="number"
                        min="0"
                        value={v.daily_total ?? ""}
                        onChange={(e) => setVendorField(key, "daily_total", e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        className="admindash-usage-input"
                        type="number"
                        min="0"
                        value={v.per_ip_daily ?? ""}
                        onChange={(e) => setVendorField(key, "per_ip_daily", e.target.value)}
                      />
                    </td>
                    <td>{u.rejected || 0}</td>
                    <td>{u.nokey == null ? "—" : u.nokey}</td>
                    <td>
                      {u.distinct_ips ? (
                        <button className="admindash-usage-link" onClick={() => setShowIpsFor(showIpsFor === key ? "" : key)}>
                          {u.distinct_ips}
                        </button>
                      ) : (
                        0
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {showIpsFor !== "" && (
            <div className="admindash-usage-ips">
              <strong>Busiest networks today — {VENDORS.find((v) => v.key === showIpsFor)?.label}</strong>
              <ul>
                {(today.vendors[showIpsFor]?.top_ips || []).map((ip, i) => (
                  <li key={i}>
                    {ip.label} — {ip.n}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="tai-check-row" onClick={() => setLimits((p) => ({ ...p, require_key: !p.require_key }))}>
            <div className={`tai-check-box${limits.require_key ? " checked" : ""}`}>{limits.require_key && <span>✓</span>}</div>
            <span className="tai-check-label">
              Require app key — rejects requests without it. Turn on only once older app versions (which don't send the key) are
              no longer in use; watch the "No key" column first.
              {!info.keys_configured ? " (No app key is configured on the server yet.)" : ""}
            </span>
          </div>

          <div className="admindash-lookup-row">
            <button className="tai-primary-btn" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save limits"}
            </button>
            <button className="tai-cancel-btn" onClick={load} disabled={loading}>
              {loading ? "Refreshing…" : "Refresh usage"}
            </button>
            {msg !== "" && <span className="tai-paste-hint">{msg}</span>}
          </div>

          <p className="tai-paste-title admindash-lookup-title" style={{ marginTop: 16 }}>
            Last 7 days — allowed / rejected (calls per active user)
          </p>
          <p className="tai-paste-hint">
            Active users = signed-in accounts with any activity that day (guests aren't counted). Calls per user rising
            much faster than users usually means abuse or a bug, not growth.
          </p>
          <table className="admindash-usage-table">
            <thead>
              <tr>
                <th>Date (UTC)</th>
                <th>Active users</th>
                {VENDORS.map(({ key, label }) => (
                  <th key={key}>{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {days.map((d) => (
                <tr key={d.date}>
                  <td>{d.date}</td>
                  <td>{activeUsersByDay[d.date]?.size || 0}</td>
                  {VENDORS.map(({ key }) => {
                    const u = d.vendors[key];
                    const active = activeUsersByDay[d.date]?.size || 0;
                    const perUser = u && active > 0 ? ` (${(u.total / active).toFixed(1)})` : "";
                    return <td key={key}>{u ? `${u.total} / ${u.rejected}${perUser}` : "—"}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

export default UsageLimitsPanel;
